/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { colors, medium, mediumInter } from '@/styles';
import { ArrowBig } from '@/svgs';
import { ClickableElement } from '@/atoms';

export type CommentDetailsProps = {
  className?: string;
  comment: string;
  expanded: boolean;
  onMetricTriggered: ((metric: string) => void) | null;
  toggleExpanded: VoidFunction;
};

/**
 * CommentDetails component
 */
const CommentDetails = ({
  className = '',
  comment,
  toggleExpanded,
  expanded,
  onMetricTriggered = () => {},
}: CommentDetailsProps) => {
  const [elementIsOverflowing, setElementIsOverflowing] = useState(false);
  const commentRef = useRef<HTMLDivElement>(null);

  const isElementOverflowing = useCallback(
    () =>
      Boolean(
        commentRef.current &&
          (commentRef.current.scrollHeight > commentRef.current.clientHeight ||
            commentRef.current.offsetWidth < commentRef.current.scrollWidth),
      ),
    [],
  );

  useEffect(() => {
    if (commentRef && isElementOverflowing()) {
      setElementIsOverflowing(true);
    }
  }, [setElementIsOverflowing, comment, isElementOverflowing]);

  const onCommentClick = useCallback(() => {
    if (elementIsOverflowing) {
      if (!expanded) {
        onMetricTriggered?.('click.livefire.comment_expand');
      }
      toggleExpanded();
    }
  }, [elementIsOverflowing, expanded, toggleExpanded, onMetricTriggered]);

  return (
    <ClickableElement
      className={`${className} ${expanded ? 'expanded' : ''}`}
      onClick={onCommentClick}
    >
      <div className="comment" ref={commentRef}>
        {comment}
      </div>
      {elementIsOverflowing && (
        <div className="expand-arrow">
          <ArrowBig
            title="Click to expand comment"
            direction={expanded ? 'up' : 'down'}
            size={`${7 / 2.85}`}
          />
        </div>
      )}
    </ClickableElement>
  );
};

const styledCommentDetails = styled(CommentDetails)`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 1.5;
  overflow: hidden;
  max-height: 36px;
  cursor: pointer;

  .comment {
    ${({ theme }) => (theme.isRebrand ? medium() : mediumInter())};
    flex-basis: 94%;
    color: ${colors.boulder};
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-style: italic;
  }

  .expand-arrow {
    text-align: center;
    flex-basis: 4%;
    align-self: flex-start;

    &:focus {
      outline: none;
    }

    svg {
      max-height: 12px;
    }
  }

  &.expanded {
    height: auto;
    max-height: unset;
    overflow: auto;

    .comment {
      text-overflow: unset;
      white-space: unset;
      overflow: visible;
      align-self: baseline;
    }

    .expand-arrow {
      align-self: baseline;
    }
  }
`;

export default withTheme(styledCommentDetails);
